<template>
  <div class="gravity-viz-3d">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>E<sub>p</sub> = mgh | E<sub>k</sub> = ½mv²</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="simulation-stage">
        <div class="hud-panel">
          <div class="hud-item blue">
             <label>高度 (h)</label>
             <span class="val">{{ ballY.toFixed(1) }} <small>m</small></span>
          </div>
          <div class="hud-item pink">
             <label>速度 (v)</label>
             <span class="val">{{ velocity.toFixed(1) }} <small>m/s</small></span>
          </div>
        </div>
        
        <div class="energy-overlay">
          <div class="energy-label">能量分配</div>
          <div class="energy-bar-container">
            <div class="energy-column">
              <div class="energy-bar-wrap">
                <div class="energy-fill potential" :style="{ height: epPercent + '%' }"></div>
              </div>
              <span class="energy-tick">势能</span>
            </div>
            <div class="energy-column">
              <div class="energy-bar-wrap">
                <div class="energy-fill kinetic" :style="{ height: ekPercent + '%' }"></div>
              </div>
              <span class="energy-tick">动能</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <div class="control-item">
              <div class="label-row">
                 <span class="label">重力加速度 (g)</span>
                 <span class="val">{{ g }} m/s²</span>
              </div>
              <el-slider v-model="g" :min="1" :max="25" :step="0.1" size="small"></el-slider>
           </div>
           <div class="control-item">
              <div class="label-row">
                 <span class="label">初始高度 (H)</span>
                 <span class="val">{{ maxHeight }} m</span>
              </div>
              <el-slider v-model="maxHeight" :min="10" :max="100" size="small"></el-slider>
           </div>
        </div>

        <div class="action-footer">
           <el-button type="primary" size="small" :disabled="isDropping" @click="startDrop" icon="el-icon-video-play">开始投放</el-button>
           <el-button size="small" type="info" plain @click="reset" icon="el-icon-refresh-right">重置实验</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'GravityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      g: 9.8,
      maxHeight: 60,
      ballY: 60,
      velocity: 0,
      isDropping: false,
      running: true
    }
  },
  computed: {
    epPercent() { return (this.ballY / this.maxHeight) * 100; },
    ekPercent() {
      const vMax = Math.sqrt(2 * this.g * this.maxHeight);
      return Math.min(100, Math.pow(this.velocity / vMax, 2) * 100);
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf8fafc);
      this.camera = new THREE.PerspectiveCamera(40, container.clientWidth / container.clientHeight, 1, 1000);
      this.camera.position.set(60, 45, 90);
      this.camera.lookAt(0, 20, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);

      this.scene.add(new THREE.GridHelper(200, 20, 0xe2e8f0, 0xf1f5f9)).position.y = 0;

      const ballGeo = new THREE.SphereGeometry(3.5, 32, 32);
      const ballMat = new THREE.MeshPhongMaterial({ color: 0x2563eb, emissive: 0x1e3a8a, emissiveIntensity: 0.1 });
      this.ball = new THREE.Mesh(ballGeo, ballMat);
      this.scene.add(this.ball);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(50, 100, 50);
      this.scene.add(light);

      this.shadow = new THREE.Mesh(new THREE.CircleGeometry(3.5, 32), new THREE.MeshBasicMaterial({ color: 0x000, transparent: true, opacity: 0.15 }));
      this.shadow.rotation.x = -Math.PI / 2;
      this.shadow.position.y = 0.5;
      this.scene.add(this.shadow);

      this.animate();
    },
    startDrop() { 
      this.reset();
      this.isDropping = true; 
    },
    reset() {
      this.isDropping = false;
      this.ballY = this.maxHeight;
      this.velocity = 0;
    },
    animate() {
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);
      if (this.isDropping) {
        const dt = 0.016;
        this.velocity += this.g * dt;
        this.ballY -= this.velocity * dt * 5;
        if (this.ballY <= 3.5) {
          this.ballY = 3.5;
          this.velocity = -this.velocity * 0.4;
          if (Math.abs(this.velocity) < 1) { this.velocity = 0; this.isDropping = false; }
        }
      }
      this.ball.position.y = this.ballY;
      const s = Math.max(0.1, 1 - (this.ballY / 100));
      this.shadow.scale.set(s*1.2, s*1.2, 1);
      this.shadow.material.opacity = 0.2 * s;
      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.gravity-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { 
  flex: 1; min-height: 220px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden; 
  .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.pink { border-left: 3px solid #f43f5e; } } }
    .energy-overlay {
      position: absolute; right: 12px; top: 12px; bottom: 12px; width: 80px; background: rgba(255,255,255,0.9); border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 8px; display: flex; flex-direction: column; align-items: center; gap: 8px; z-index: 10;
      .energy-label { font-size: 11px; font-weight: 800; color: #475569; margin-bottom: 4px; }
      .energy-bar-container { flex: 1; display: flex; gap: 12px; align-items: flex-end; width: 100%; justify-content: center; }
      .energy-column { display: flex; flex-direction: column; align-items: center; gap: 4px; height: 100%; justify-content: flex-end; }
      .energy-bar-wrap { width: 12px; flex: 1; background: #f1f5f9; border-radius: 6px; position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: flex-end; }
      .energy-fill { width: 100%; transition: height 0.1s ease-out; &.potential { background: #f43f5e; box-shadow: 0 0 8px rgba(244, 63, 94, 0.3); } &.kinetic { background: #10b981; box-shadow: 0 0 8px rgba(16, 185, 129, 0.3); } }
      .energy-tick { font-size: 9px; color: #64748b; font-weight: 700; }
    }
  }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .control-item { .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } } } }
.action-footer { display: flex; gap: 8px; .el-button { flex: 1; font-weight: 700; } }
</style>
